(() => {
  'use strict';
  const VERSION = '2.1.1';
  if (window.AWConfiguratorRelationGuard?.version === VERSION) return;

  const CONFIG_KEY = 'aw_airsoft_configurator';
  const CANONICAL = new Map([
    ['compatible', 'Compatible'],
    ['a-verifier', 'À vérifier'],
    ['incompatible', 'Incompatible'],
    ['compatible-sous-condition', 'Compatible sous condition'],
    ['necessite-adaptation', 'Nécessite adaptation']
  ]);

  const norm = (value = '') => String(value).trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const split = (value = '') => String(value).split(',').map(norm).filter(Boolean);
  const readJson = (key, fallback = {}) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch (_) { return fallback; }
  };
  const clean = (value = '', max = 220) => String(value || '').trim().replace(/\s+/g, ' ').slice(0, max);
  const sourceFor = (root, variantId) => Array.from(root?.querySelectorAll?.('[data-aw-config-product]') || [])
    .find((node) => String(node.dataset.variantId || '') === String(variantId || ''));

  const contextFor = (root) => {
    const state = readJson(root?.dataset.storageKey || CONFIG_KEY, {});
    let shared = {};
    try { shared = window.AWAirsoftContext?.current?.() || {}; } catch (_) {}
    return {
      platform: norm(state.platform || shared.platform || ''),
      models: new Set([shared.model, shared.modelLabel, state.model, state.modelLabel].map(norm).filter(Boolean))
    };
  };

  const relationsFor = (source) => Array.from(source?.querySelectorAll?.('[data-aw-config-relation]') || []).map((node) => ({
    platform: norm(node.dataset.platform || ''),
    model: norm(node.dataset.model || ''),
    variantId: String(node.dataset.variantId || ''),
    status: CANONICAL.get(norm(node.dataset.status || '')) || '',
    condition: clean(node.dataset.condition),
    incompatibilityReason: clean(node.dataset.incompatibilityReason),
    adaptation: clean(node.dataset.adaptation),
    generation: clean(node.dataset.generation),
    proof: clean(node.dataset.proof),
    date: clean(node.dataset.verificationDate, 40),
    score: Number(node.dataset.confidenceScore || 0)
  }));

  const firstDetail = (relations, key) => relations.map((relation) => relation[key]).find(Boolean) || '';
  const review = (source, label, conflict = false) => ({
    verdict: 'À vérifier', key: 'review', source, blocked: false, warning: true, conflict,
    label: label || 'Correspondance plateforme — à vérifier', detail: ''
  });

  const evaluate = (root, source, variantId = '') => {
    const context = contextFor(root);
    if (!source) return review('missing-source', 'À vérifier');
    const platform = context.platform;
    const variant = String(variantId || source.dataset.variantId || '');

    const explicit = relationsFor(source).filter((relation) => {
      if (!relation.status) return false;
      if (relation.platform && relation.platform !== platform) return false;
      if (relation.variantId && relation.variantId !== variant) return false;
      if (relation.model && !context.models.has(relation.model)) return false;
      return true;
    });

    if (explicit.length) {
      const verdicts = [...new Set(explicit.map((relation) => relation.status))];
      if (verdicts.length !== 1) {
        return { ...review('explicit-relation', 'Conflit de verdicts AW — à vérifier', true), relations: explicit };
      }
      const verdict = verdicts[0];
      const result = {
        verdict, key: norm(verdict), source: 'explicit-relation', blocked: verdict === 'Incompatible',
        warning: verdict !== 'Compatible', conflict: false, relations: explicit, label: verdict, detail: ''
      };
      if (verdict === 'À vérifier') result.label = 'À vérifier — sélection autorisée';
      if (verdict === 'Compatible sous condition') result.detail = firstDetail(explicit, 'condition');
      if (verdict === 'Nécessite adaptation') result.detail = firstDetail(explicit, 'adaptation');
      if (verdict === 'Incompatible') result.detail = firstDetail(explicit, 'incompatibilityReason');
      return result;
    }

    const exactModels = new Set(split(source.dataset.models || ''));
    if (context.models.size && [...context.models].some((model) => exactModels.has(model))) {
      return review('exact-model', 'Correspondance modèle exact — à vérifier');
    }

    const platforms = new Set(split(source.dataset.platforms || ''));
    if (platform && platforms.has(platform)) return review('platform', 'Correspondance plateforme — à vérifier');

    return {
      verdict: 'À vérifier', key: 'platform-mismatch', source: 'platform-mismatch', blocked: true,
      warning: true, conflict: false, label: 'Hors plateforme sélectionnée', detail: ''
    };
  };

  const display = (result) => {
    if (result.key === 'compatible') return { badge: 'Compatible AW', note: 'Relation Compatibility AW explicite.', title: 'Compatible selon une relation Compatibility AW explicite.' };
    if (result.key === 'incompatible') return { badge: 'Incompatible', note: result.detail || 'Incompatible avec la configuration sélectionnée.', title: result.detail || 'Sélection bloquée : relation Compatibility AW explicitement incompatible.' };
    if (result.key === 'compatible-sous-condition') return { badge: 'Compatible sous condition', note: result.detail || 'Sélection autorisée sous condition.', title: result.detail || 'Sélection autorisée sous condition.' };
    if (result.key === 'necessite-adaptation') return { badge: 'Nécessite adaptation', note: result.detail || 'Sélection autorisée avec adaptation.', title: result.detail || 'Sélection autorisée ; adaptation requise.' };
    if (result.conflict) return { badge: 'À vérifier', note: 'Conflit de verdicts AW : aucune résolution automatique.', title: 'Sélection autorisée ; conflit de verdicts AW à vérifier.' };
    if (result.source === 'exact-model') return { badge: 'À vérifier', note: 'Correspondance modèle exact — à vérifier', title: 'Correspondance modèle exact — à vérifier.' };
    if (result.source === 'platform') return { badge: 'À vérifier', note: 'Correspondance plateforme — à vérifier', title: 'Correspondance plateforme — à vérifier.' };
    if (result.source === 'platform-mismatch') return { badge: 'À vérifier', note: 'Hors plateforme sélectionnée', title: 'Sélection bloquée : ce composant n’appartient pas à la plateforme choisie.' };
    return { badge: 'À vérifier', note: 'Données relationnelles à vérifier', title: 'Sélection autorisée avec vérification recommandée.' };
  };

  const ensureUi = (root, card, result) => {
    if (!card) return;
    const info = display(result);
    card.dataset.awRelationalVerdict = result.key;
    card.dataset.awRelationalSource = result.source;

    let badge = card.querySelector('[data-aw-relational-badge]');
    if (!badge) {
      badge = document.createElement('p');
      badge.className = 'aw-configurator__product-badge aw-configurator__relational-badge';
      badge.dataset.awRelationalBadge = 'true';
      const body = card.querySelector('.aw-configurator__product-body');
      const legacy = body?.querySelector('.aw-configurator__product-badge:not([data-aw-relational-badge])');
      if (legacy) legacy.insertAdjacentElement('afterend', badge); else body?.prepend(badge);
    }
    const badgeBody = card.querySelector('.aw-configurator__product-body');
    const legacyBadge = badgeBody?.querySelector('.aw-configurator__product-badge:not([data-aw-relational-badge])');
    if (badge && legacyBadge && legacyBadge.nextElementSibling !== badge) legacyBadge.insertAdjacentElement('afterend', badge);
    if (badge.textContent !== info.badge) badge.textContent = info.badge;

    let note = card.querySelector('[data-aw-relational-label]');
    if (!note) {
      note = document.createElement('small');
      note.className = 'aw-configurator__relational-label';
      note.dataset.awRelationalLabel = 'true';
      const body = card.querySelector('.aw-configurator__product-body');
      const heading = body?.querySelector('h3');
      if (heading) heading.insertAdjacentElement('afterend', note); else body?.append(note);
    }
    if (note.textContent !== info.note) note.textContent = info.note;

    const toggle = card.querySelector('[data-aw-config-toggle]');
    if (toggle) {
      const disabled = Boolean(result.blocked);
      if (toggle.disabled !== disabled) toggle.disabled = disabled;
      const aria = disabled ? 'true' : 'false';
      if (toggle.getAttribute('aria-disabled') !== aria) toggle.setAttribute('aria-disabled', aria);
      if (toggle.title !== info.title) toggle.title = info.title;
    }
  };

  const applyCards = (root) => root?.querySelectorAll?.('[data-aw-rendered-product]').forEach((card) => {
    const variantId = card.dataset.awRenderedProduct || '';
    ensureUi(root, card, evaluate(root, sourceFor(root, variantId), variantId));
  });

  const selectedIds = (root) => {
    const state = readJson(root.dataset.storageKey || CONFIG_KEY, {});
    return Array.isArray(state.selected) ? state.selected.map(String) : [];
  };

  const removeCurrent = (root, variantId) => {
    const products = root.querySelector('[data-aw-config-products]');
    if (!products) return false;
    const trigger = document.createElement('button');
    trigger.type = 'button'; trigger.hidden = true;
    trigger.dataset.awConfigToggle = String(variantId);
    trigger.dataset.awRelationRemoval = 'true';
    products.appendChild(trigger); trigger.click(); trigger.remove?.();
    return true;
  };

  const showNotice = (root, message, status = '') => {
    const notice = root.querySelector('[data-aw-config-notice]');
    if (!notice) return;
    notice.hidden = !message; notice.textContent = message;
    notice.classList.toggle('is-error', status === 'error');
    notice.classList.toggle('is-success', status === 'success');
  };

  const sanitizeCurrent = (root, options = {}) => {
    const blocked = selectedIds(root).map((variantId) => ({
      variantId, result: evaluate(root, sourceFor(root, variantId), variantId)
    })).filter((entry) => entry.result.blocked);
    let removed = 0;
    blocked.forEach(({ variantId }) => { if (removeCurrent(root, variantId)) removed += 1; });
    if (removed && options.notice !== false) {
      showNotice(root, `${removed} composant${removed > 1 ? 's bloquants ont' : ' bloquant a'} été retiré${removed > 1 ? 's' : ''} uniquement de la configuration courante. Les configurations sauvegardées restent intactes.`, 'error');
    }
    return { removed, blocked };
  };

  const bridgeLegacy = (root) => {
    const context = contextFor(root);
    if (!context.platform) return 0;
    let changed = 0;
    root.querySelectorAll('[data-aw-config-product]').forEach((source) => {
      const result = evaluate(root, source, source.dataset.variantId || '');
      if (result.blocked || !['explicit-relation', 'exact-model'].includes(result.source)) return;
      const platforms = new Set(split(source.dataset.platforms || ''));
      if (platforms.has(context.platform)) return;
      platforms.add(context.platform);
      source.dataset.platforms = [...platforms].join(',');
      source.dataset.awRelationalLegacyBridge = 'true';
      changed += 1;
    });
    if (changed && root.dataset.awReady === 'true') {
      root.querySelector('[data-aw-config-search]')?.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return changed;
  };

  const installStyle = () => {
    if (document.querySelector('[data-aw-relational-guard-style]')) return;
    const style = document.createElement('style');
    style.dataset.awRelationalGuardStyle = 'true';
    style.textContent = `[data-aw-configurator][data-aw-relational-guard-installed="true"] .aw-configurator__product-badge:not([data-aw-relational-badge]){display:none!important}[data-aw-configurator][data-aw-relational-guard-installed="true"] [data-aw-p0-verification-label]{display:none!important}[data-aw-relational-label]{display:block;margin:.3rem 0 .45rem;opacity:.82}[data-aw-relational-verdict="incompatible"] [data-aw-relational-badge],[data-aw-relational-verdict="platform-mismatch"] [data-aw-relational-badge]{font-weight:700}`;
    document.head.appendChild(style);
  };

  const refreshRoot = (root, sanitize = true) => {
    bridgeLegacy(root);
    applyCards(root);
    if (sanitize && root.dataset.awReady === 'true') {
      sanitizeCurrent(root, { notice: false });
      applyCards(root);
    }
  };

  let documentGuardInstalled = false;
  const installDocumentGuard = () => {
    if (documentGuardInstalled) return;
    documentGuardInstalled = true;
    document.addEventListener('click', (event) => {
      const root = event.target.closest?.('[data-aw-configurator]');
      if (!root || root.dataset.awRelationalGuardInstalled !== 'true') return;

      const toggle = event.target.closest?.('[data-aw-config-toggle]');
      if (toggle && toggle.dataset.awRelationRemoval !== 'true') {
        const variantId = String(toggle.dataset.awConfigToggle || '');
        const result = evaluate(root, sourceFor(root, variantId), variantId);
        if (result.blocked) {
          event.preventDefault(); event.stopImmediatePropagation();
          const wasSelected = selectedIds(root).includes(variantId);
          if (wasSelected) removeCurrent(root, variantId);
          const message = result.key === 'incompatible'
            ? (wasSelected ? 'Composant Incompatible AW retiré uniquement de la configuration courante. La sauvegarde d’origine reste intacte.' : `Sélection bloquée : ${result.detail || 'relation Compatibility AW explicitement Incompatible.'}`)
            : (wasSelected ? 'Composant hors plateforme retiré uniquement de la configuration courante. La sauvegarde d’origine reste intacte.' : 'Sélection bloquée : ce composant n’appartient pas à la plateforme choisie.');
          showNotice(root, message, 'error');
          return;
        }
      }

      const addAll = event.target.closest?.('[data-aw-config-add-all]');
      if (addAll) {
        const check = sanitizeCurrent(root, { notice: false });
        if (check.removed) {
          event.preventDefault(); event.stopImmediatePropagation();
          showNotice(root, `${check.removed} composant${check.removed > 1 ? 's bloquants ont' : ' bloquant a'} été retiré${check.removed > 1 ? 's' : ''} de la configuration courante. Vérifiez le récapitulatif avant d’ajouter le reste au panier. Les sauvegardes restent intactes.`, 'error');
        }
      }
    }, true);
  };

  const install = (scope = document) => {
    installStyle();
    installDocumentGuard();
    const roots = [];
    if (scope.matches?.('[data-aw-configurator]')) roots.push(scope);
    scope.querySelectorAll?.('[data-aw-configurator]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.awRelationalGuardInstalled === 'true') { refreshRoot(root); return; }
      root.dataset.awRelationalGuardInstalled = 'true';

      root.addEventListener('click', (event) => {
        const toggle = event.target.closest?.('[data-aw-config-toggle]');
        if (toggle && toggle.dataset.awRelationRemoval !== 'true') {
          const variantId = String(toggle.dataset.awConfigToggle || '');
          const result = evaluate(root, sourceFor(root, variantId), variantId);
          if (result.blocked) {
            event.preventDefault(); event.stopImmediatePropagation();
            const selected = selectedIds(root).includes(variantId);
            if (selected) removeCurrent(root, variantId);
            const message = result.key === 'incompatible'
              ? (selected ? 'Composant Incompatible AW retiré uniquement de la configuration courante. La sauvegarde d’origine reste intacte.' : `Sélection bloquée : ${result.detail || 'relation Compatibility AW explicitement Incompatible.'}`)
              : (selected ? 'Composant hors plateforme retiré uniquement de la configuration courante. La sauvegarde d’origine reste intacte.' : 'Sélection bloquée : ce composant n’appartient pas à la plateforme choisie.');
            showNotice(root, message, 'error');
            return;
          }
          if (result.conflict) showNotice(root, 'Sélection autorisée : conflit de verdicts AW détecté. Le résultat reste À vérifier, sans résolution automatique.');
          else if (result.key === 'compatible-sous-condition') showNotice(root, `Sélection autorisée sous condition${result.detail ? ` : ${result.detail}` : '.'}`);
          else if (result.key === 'necessite-adaptation') showNotice(root, `Sélection autorisée avec adaptation${result.detail ? ` : ${result.detail}` : '.'}`);
          else if (result.verdict === 'À vérifier') showNotice(root, `${display(result).note}. Sélection autorisée.`);
        }

        const addAll = event.target.closest?.('[data-aw-config-add-all]');
        if (addAll) {
          const check = sanitizeCurrent(root, { notice: false });
          if (check.removed) {
            event.preventDefault(); event.stopImmediatePropagation();
            showNotice(root, `${check.removed} composant${check.removed > 1 ? 's bloquants ont' : ' bloquant a'} été retiré${check.removed > 1 ? 's' : ''} de la configuration courante. Vérifiez le récapitulatif avant d’ajouter le reste au panier. Les sauvegardes restent intactes.`, 'error');
          }
        }
      }, true);

      let scheduled = false;
      const observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        queueMicrotask(() => { scheduled = false; bridgeLegacy(root); applyCards(root); });
      });
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      refreshRoot(root, false);

      const ready = (attempt = 0) => {
        if (root.dataset.awReady === 'true') { refreshRoot(root, true); return; }
        if (attempt < 30) setTimeout(() => ready(attempt + 1), 100);
      };
      ready();
    });
  };

  const refreshAll = () => {
    install(document);
    document.querySelectorAll('[data-aw-configurator]').forEach((root) => refreshRoot(root, true));
  };

  window.addEventListener('aw:context-changed', refreshAll);
  window.addEventListener('aw:platform-changed', refreshAll);
  window.addEventListener('aw:compatibility-changed', refreshAll);
  document.addEventListener('shopify:section:load', (event) => install(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => install(document), { once: true });
  else install(document);

  window.AWConfiguratorRelationGuard = {
    version: VERSION,
    policy: 'explicit-relation>exact-model>platform',
    evaluate, sanitizeCurrent, bridgeLegacy, install, refresh: refreshAll
  };
})();
